// src/components/ZodiacPhotoFrame.tsx
// ============================================================================
// FILE ROLE: Standalone viral sharing component. User picks a sign + caption,
// previews the frame design, opens their camera, captures a 1080×1920 PNG with
// the branded overlay composited in real time, then shares via Web Share API
// (with download fallback).
//
// No backend. No new deps. Single-file delivery. Camera permission is granted
// upstream by AgeVerificationGate, so the getUserMedia call is no-friction.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from "react";
import { Muxer, ArrayBufferTarget } from "mp4-muxer";
import { logEvent } from "@/utils/analytics";
import { filterCaption, CAPTION_MAX_CHARS } from "@/utils/captionFilter";

// ============================================================================
// TYPES
// ============================================================================

export type ZodiacSign =
  | "Aries" | "Taurus" | "Gemini" | "Cancer" | "Leo" | "Virgo"
  | "Libra" | "Scorpio" | "Sagittarius" | "Capricorn" | "Aquarius" | "Pisces";

type Step = "style" | "camera" | "edit" | "review";

export interface ZodiacPhotoFrameProps {
  onShareComplete?: () => void;
  /** Called when the user explicitly dismisses (✕ or Back) without completing a share.
   *  Separates cancel from share-completion so callers can scroll to Join only on success. */
  onDismiss?: () => void;
  /** When provided, the parent owns sign selection — the internal sign-picker
   *  step is skipped and the flow starts at caption selection. The component
   *  stays in sync with this prop if the parent changes it. */
  selectedSign?: ZodiacSign;
}

// ============================================================================
// CONSTANTS
// ============================================================================

// Opener preset options — all lowercase; order is locked.
const OPENER_OPTIONS = [
  "if you know you know",
  "took me long enough",
  "never had the nerve till now",
  "okay, deep breath",
  "only saying this once",
  "the urge to delete this is high",
  "i did my part",
  "u see the vision or what",
  "officially out of retirement",
  "manifesting this works",
  "balls in your court"

] as const;

// Sentinel value used as the "write your own" <option> value.
// Never stored in OPENER_OPTIONS so it can neverleak into caption assembly.
// The onChange handler intercepts it and switches the slot to the custom <input>.
const CUSTOM_OPTION_VALUE = "__custom__";

// Output dimensions for the composited PNG. 9:16 portrait, matches social
// stories aspect ratio (IG/TikTok/Snap).
const CANVAS_W = 1080;
const CANVAS_H = 1920;

const MAX_RECORD_SECONDS = 6;
// Set to true to restore caption overlay in camera view and recording composite.
const DRAW_CAPTION_IN_RECORDING = false;

// Video recording is parked pending WebCodecs encoder migration.
// Flip to true (and redeploy) to restore the Record button and all video UI.
const VIDEO_BOOTH_ENABLED = false;

const VIDEO_CODEC    = "avc1.420028";
const VIDEO_BITRATE  = 4_000_000;
const VIDEO_FPS      = 30;
const KEYFRAME_EVERY = 60;

// VideoEncoderConfig does not include the codec-specific `avc` field in TS DOM lib.
interface VideoEncoderConfigExt extends VideoEncoderConfig {
  avc?: { format: "avc" | "annexb" };
}



// Example photos for the preview carousel. Cycled through by exampleIdx on
// the "preview" step so the user sees what a real captured frame looks like.
// Served from /public — Vite/CRA copy them to the site root at build time.
const PLACEHOLDER_BGS: ReadonlyArray<string> = [
  "/preview1.png",
  "/preview2.png",
  "/preview3.png",
  "/preview4.png",
];

interface PreviewPosition {
  bottom?: string;
  top?: string;
  left: string;
  right: string;
  textAlign: "center" | "left" | "right";
}

// Named placement presets for the auto-cycling preview carousel.
const PREVIEW_POSITIONS: readonly PreviewPosition[] = [
  { bottom: "28%", left: "6%",  right: "6%",  textAlign: "center" }, // lower-third
  { top:    "14%", left: "6%",  right: "6%",  textAlign: "center" }, // upper-third
  { bottom: "42%", left: "6%",  right: "26%", textAlign: "left"   }, // offset-left
  { bottom: "42%", left: "26%", right: "6%",  textAlign: "right"  }, // offset-right
];

// All 5 caption-tool swatches. Dual text-shadow (light + dark halo) keeps
// black legible on dark scrim and light colors legible on bright photo areas.
const PREVIEW_COLORS = ["#FFFFFF", "#1A1A1A", "#FFE600", "#E05A3A", "#F0DFC8"] as const;

const BRAND = {
  coral:  "#E05A3A",
  cream:  "#F0DFC8",
  ink:    "#1A1A1A",
  pageBg: "#FDF6EE",
  white:  "#FFFFFF",
} as const;

// ============================================================================
// HELPERS
// ============================================================================


// Maximum vertical extent of the caption block within the scrim zone.
// Prevents worst-case custom text (50 chars × 2 fields) from running off-frame.
const MAX_CAPTION_BLOCK_H = Math.round(CANVAS_H * 0.55); // 1056px on a 1920px canvas
const CAPTION_MIN_FONT    = 28; // never shrink below this; text remains legible

// Draws a multi-line caption where \n marks hard line breaks.
// Each segment is independently word-wrapped. Font size is reduced in 4px
// steps when the wrapped block would exceed MAX_CAPTION_BLOCK_H — this is
// the overflow guard that keeps worst-case custom captions on-frame.
// Sets ctx.font internally; callers must set fillStyle/textAlign/textBaseline
// but must NOT set ctx.font before calling.
function drawCaptionText(
  ctx: CanvasRenderingContext2D,
  text: string,
  centerX: number,
  centerY: number,
  maxWidth: number,
  baseFontSize: number,
  baseLineHeight: number,
): void {
  const lhRatio = baseLineHeight / baseFontSize;

  function buildLines(mw: number): string[] {
    const result: string[] = [];
    for (const hard of text.split("\n")) {
      const words = hard.split(/\s+/).filter(Boolean);
      if (!words.length) { result.push(""); continue; }
      let cur = "";
      for (const w of words) {
        const test = cur ? `${cur} ${w}` : w;
        if (ctx.measureText(test).width > mw && cur) {
          result.push(cur);
          cur = w;
        } else { cur = test; }
      }
      if (cur) result.push(cur);
    }
    return result;
  }

  let fontSize   = baseFontSize;
  let lineHeight = baseLineHeight;

  // Shrink until block fits within MAX_CAPTION_BLOCK_H.
  while (fontSize > CAPTION_MIN_FONT) {
    ctx.font = `800 ${fontSize}px system-ui, -apple-system, sans-serif`;
    const lines = buildLines(maxWidth);
    lineHeight = Math.round(fontSize * lhRatio);
    if (lines.length * lineHeight <= MAX_CAPTION_BLOCK_H) break;
    fontSize = Math.max(CAPTION_MIN_FONT, fontSize - 4);
  }

  const lines  = buildLines(maxWidth);
  const totalH = lines.length * lineHeight;
  const startY = centerY - totalH / 2 + lineHeight / 2;
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], centerX, startY + i * lineHeight);
  }
}

// ============================================================================
// CAPTION CONSTANTS — single source of defaults; used as state initializers.
// ============================================================================
const CAPTION_FONT_SIZE_DEFAULT = 84;
const CAPTION_CENTER_X_DEFAULT  = CANVAS_W / 2;                // 540
const CAPTION_CENTER_Y_DEFAULT  = Math.round(CANVAS_H * 0.68); // 1306

// Half-extents for position clamping. 0.12 / 0.20 are tunable feel values
// (edge margin as a fraction of canvas). The drawCaptionText font-shrink loop
// and MAX_CAPTION_BLOCK_H cap are the real backstops — do not remove them.
const CAPTION_CLAMP_HALF_W = Math.ceil(CANVAS_W * 0.12); // ~130 — 12% soft edge each side
const CAPTION_CLAMP_HALF_H = Math.ceil(CANVAS_H * 0.20); // ~384 — 20% soft edge top/bottom

function clampCaptionPos(pos: { x: number; y: number }): { x: number; y: number } {
  return {
    x: Math.max(CAPTION_CLAMP_HALF_W, Math.min(CANVAS_W - CAPTION_CLAMP_HALF_W, pos.x)),
    y: Math.max(CAPTION_CLAMP_HALF_H, Math.min(CANVAS_H - CAPTION_CLAMP_HALF_H, pos.y)),
  };
}

// Shared caption draw path — the ONLY place caption context properties and
// drawCaptionText arguments are assembled. Both paintFrame and paintOverlay
// call this; neither contains any caption draw logic directly.
// lineHeight scales proportionally with fontSize (original 96/84 ratio).
function drawCaption(
  ctx: CanvasRenderingContext2D,
  captionText: string,
  fontSize: number,
  centerX: number,
  centerY: number,
  color: string = BRAND.white,
): void {
  ctx.fillStyle    = color;
  ctx.textAlign    = "center";
  ctx.textBaseline = "middle";
  drawCaptionText(ctx, captionText, centerX, centerY, CANVAS_W - 120, fontSize, Math.round(fontSize * 96 / 84));
}

// Baked-in brand watermark drawn in the bottom-right corner of every exported image.
// Coral pill: white "crushhh [glyph] com" in Fredoka 700.
// glyph is a pre-tinted white offscreen canvas; null falls back to a filled dot.
const SIG_FONT_SIZE = 34;
const SIG_PILL_H    = 68;
const SIG_GLYPH_H   = 48;
// favicon.svg viewBox is 158.9×130.9 — maintain aspect ratio at SIG_GLYPH_H.
const SIG_GLYPH_W   = Math.round(SIG_GLYPH_H * (158.9 / 130.9));
const SIG_GAP       = 10;
const SIG_PAD_X     = 22;
const SIG_MARGIN_R  = 40;
const SIG_MARGIN_B  = 72;

function drawSignaturePill(
  ctx: CanvasRenderingContext2D,
  glyph: HTMLCanvasElement | null,
): void {
  ctx.save();
  ctx.font         = `700 ${SIG_FONT_SIZE}px "Fredoka", sans-serif`;
  ctx.textBaseline = "middle";
  ctx.textAlign    = "left";

  const w1    = ctx.measureText("crushhh").width;
  const w2    = ctx.measureText("com").width;
  const pillW = SIG_PAD_X + w1 + SIG_GAP + SIG_GLYPH_W + SIG_GAP + w2 + SIG_PAD_X;

  const pillRight  = CANVAS_W - SIG_MARGIN_R;
  const pillBottom = CANVAS_H - SIG_MARGIN_B;
  const pillLeft   = pillRight - pillW;
  const pillTop    = pillBottom - SIG_PILL_H;
  const r          = SIG_PILL_H / 2;

  ctx.beginPath();
  ctx.moveTo(pillLeft + r,  pillTop);
  ctx.lineTo(pillRight - r, pillTop);
  ctx.arcTo(pillRight,  pillTop,    pillRight,    pillTop + r,    r);
  ctx.lineTo(pillRight,    pillBottom - r);
  ctx.arcTo(pillRight,  pillBottom, pillRight - r, pillBottom,    r);
  ctx.lineTo(pillLeft + r, pillBottom);
  ctx.arcTo(pillLeft,   pillBottom, pillLeft,     pillBottom - r, r);
  ctx.lineTo(pillLeft,     pillTop + r);
  ctx.arcTo(pillLeft,   pillTop,    pillLeft + r,  pillTop,       r);
  ctx.closePath();
  ctx.fillStyle = BRAND.coral;
  ctx.fill();

  const midY = pillTop + SIG_PILL_H / 2;
  ctx.fillStyle = BRAND.white;
  let x = pillLeft + SIG_PAD_X;

  ctx.fillText("crushhh", x, midY);
  x += w1 + SIG_GAP;

  if (glyph) {
    ctx.drawImage(glyph, x, midY - SIG_GLYPH_H / 2, SIG_GLYPH_W, SIG_GLYPH_H);
  } else {
    ctx.beginPath();
    ctx.arc(x + SIG_GLYPH_W / 2, midY, 7, 0, Math.PI * 2);
    ctx.fill();
  }
  x += SIG_GLYPH_W + SIG_GAP;

  ctx.fillText("com", x, midY);
  ctx.restore();
}

// Decode the base64 payload of a "data:image/png;base64,..." URL into bytes.
// Used to build a File for the Web Share API without ever materializing a
// blob URL (URL.createObjectURL is blocked by CSP in this app).
function dataUrlToBytes(dataUrl: string): Uint8Array {
  const commaIdx = dataUrl.indexOf(",");
  if (commaIdx < 0 || !dataUrl.startsWith("data:")) {
    throw new Error("Invalid data URL");
  }
  const binary = atob(dataUrl.slice(commaIdx + 1));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function downloadDataUrl(dataUrl: string, filename: string): void {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function ZodiacPhotoFrame({ onShareComplete, onDismiss, selectedSign }: ZodiacPhotoFrameProps) {
  // -------- State --------
  // When `selectedSign` is provided, the parent owns sign selection so the
  // internal "sign" step is bypassed and the flow opens on "style".
  const signControlled = selectedSign != null;
  const [step,         setStep]         = useState<Step>("style");
  // Default to Aries so the Zodiyak mascot always renders even without a selectedSign prop.
  const [sign,         setSign]         = useState<ZodiacSign>(selectedSign ?? "Aries");
  const [opener,        setOpener]       = useState<string>(OPENER_OPTIONS[0]);
  const [facing,       setFacing]       = useState<"user" | "environment">("user");
  const [exampleIdx,   setExampleIdx]   = useState<number>(0);
  const [error,        setError]        = useState<string | null>(null);
  const [capturedUrl,  setCapturedUrl]  = useState<string | null>(null);
  const [captureSource, setCaptureSource] = useState<"camera" | "gallery" | "video" | null>(null);
  const [sharing,      setSharing]      = useState<boolean>(false);
  const [pressing,     setPressing]     = useState<boolean>(false);
  const [fontSize,       setFontSize]       = useState<number>(CAPTION_FONT_SIZE_DEFAULT);
  const [captionColor,   setCaptionColor]   = useState<string>("#FFFFFF");
  // Auto-cycling demo state — only active when user hasn't touched the opener dropdown.
  const [demoOpenerIdx,  setDemoOpenerIdx]  = useState<number>(0);
  const [demoPosIdx,     setDemoPosIdx]     = useState<number>(0);
  const [demoColorIdx,   setDemoColorIdx]   = useState<number>(0);
  const [demoFontSize,   setDemoFontSize]   = useState<number>(26); // px; base = effective value of min(7vw,26px)
  const [captionVisible, setCaptionVisible] = useState<boolean>(true);
  // captionPos drives re-renders so the drag overlay cursor updates and JSX
  // stays in sync with captionPosRef; canvas reads the ref directly on each tick.
  const [, setCaptionPos]   = useState({ x: CAPTION_CENTER_X_DEFAULT, y: CAPTION_CENTER_Y_DEFAULT });
  // Custom opener — toggled when user selects "write your own".
  const [useOpenerCustom,  setUseOpenerCustom]  = useState<boolean>(false);
  const [customOpener,     setCustomOpener]     = useState<string>("");
  const [openerFilterErr,  setOpenerFilterErr]  = useState<string | null>(null);
  const [isRecording,        setIsRecording]        = useState(false);
  const [recordSecsLeft,     setRecordSecsLeft]      = useState(0);
  const [videoSupported,     setVideoSupported]      = useState(false);
  const [videoCaptionOpener, setVideoCaptionOpener]  = useState<string>(OPENER_OPTIONS[0]);
  const [capturedVideoUrl,   setCapturedVideoUrl]    = useState<string | null>(null);

  // -------- Refs --------
  const containerRef   = useRef<HTMLDivElement | null>(null);
  const videoRef       = useRef<HTMLVideoElement | null>(null);
  const reviewVideoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef      = useRef<HTMLCanvasElement | null>(null);
  const fileInputRef   = useRef<HTMLInputElement | null>(null);
  // Separate canvas for the post-capture edit step — keeps the camera canvas
  // and its RAF/stream teardown machinery completely untouched (Brief BM/BP).
  const editCanvasRef  = useRef<HTMLCanvasElement | null>(null);
  // Pre-decoded Image of the captured still. Decoded once in handleCapture so
  // edit-step redraws call drawImage on an already-decoded object (~1-5ms each).
  const stillImageRef  = useRef<HTMLImageElement | null>(null);
  const zodiyakImgRef      = useRef<HTMLImageElement | null>(null);
  const signatureGlyphRef      = useRef<HTMLImageElement | null>(null);
  const signatureGlyphWhiteRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef              = useRef<MediaStream | null>(null);
  const rafRef         = useRef<number | null>(null);
  // Tick reads facing every frame via this ref so the mirror toggle reflects
  // the most recent flip without rebuilding the closure (and without forcing
  // the camera effect to re-acquire the stream on every flip).
  const facingRef      = useRef<"user" | "environment">("user");
  // fontSizeRef and captionPosRef are read on every RAF tick and at capture
  // time. Keeping them as refs (not deps) means slider and drag updates reach
  // the canvas immediately without rebuilding paintOverlay / paintFrame or
  // restarting the camera stream.
  const fontSizeRef      = useRef<number>(CAPTION_FONT_SIZE_DEFAULT);
  const captionColorRef  = useRef<string>("#FFFFFF");
  const captionPosRef    = useRef({ x: CAPTION_CENTER_X_DEFAULT, y: CAPTION_CENTER_Y_DEFAULT });
  const isDragging     = useRef<boolean>(false);
  const dragOffsetRef  = useRef({ x: 0, y: 0 });
  const redrawPendingRef = useRef(false);
  const isRecordingRef        = useRef(false);
  // Diagnostic counters — reset each recording; read in on-screen readout.
  const recFramesRef          = useRef(0);
  const recDrewVideoRef       = useRef(0);
  const recVwRef              = useRef(0);
  // WebCodecs recording
  const videoEncoderRef       = useRef<VideoEncoder | null>(null);
  const muxerRef              = useRef<Muxer<ArrayBufferTarget> | null>(null);
  const recordStartTimeRef    = useRef<number>(0);
  const lastEncodeTimeRef     = useRef<number>(0);
  const encFrameCountRef      = useRef<number>(0);
  const finalizeRecordingRef  = useRef<(() => Promise<void>) | null>(null);
  const countdownIntervalRef  = useRef<ReturnType<typeof setInterval> | null>(null);
  const videoCaptionOpenerRef = useRef<string>(OPENER_OPTIONS[0]);
  const videoObjectUrlRef     = useRef<string | null>(null);
  const videoBlobRef          = useRef<Blob | null>(null);

  // -------- Derived --------
  // Active opener: custom when toggled, otherwise the dropdown selection.
  // Empty custom input falls back to the first preset so the frame always has content.
  // The sentinel guard ensures CUSTOM_OPTION_VALUE never reaches the canvas.
  const openerValue = useOpenerCustom
    ? (customOpener.trim() || OPENER_OPTIONS[0])
    : (opener === CUSTOM_OPTION_VALUE ? OPENER_OPTIONS[0] : opener);
  // Two lines: opener (user-chosen) + locked anchor.
  const captionText = `${openerValue}\ncrushhh me back`;

  // True once the user has actively changed from the default opener. Gates
  // whether the carousel shows live user selection vs. auto-cycling demo.
  const hasInteracted = opener !== OPENER_OPTIONS[0] || useOpenerCustom;

  // Preview carousel display values — live user selection when interacted, else demo.
  const previewOpener = hasInteracted ? openerValue          : OPENER_OPTIONS[demoOpenerIdx];
  const previewPos    = hasInteracted
    ? { bottom: "28%", top: undefined, left: "6%", right: "6%", textAlign: "center" as const }
    : PREVIEW_POSITIONS[demoPosIdx];
  const previewColor  = hasInteracted ? BRAND.white          : PREVIEW_COLORS[demoColorIdx];

  // ===========================================================================
  // Keep internal sign state in sync with the controlled `selectedSign` prop.
  // If the parent swaps the sign while the user is mid-flow (e.g. tapped a
  // different picker pill), update underneath them — they're already past the
  // internal sign step and the caption rebuilds from the new sign automatically.
  // ===========================================================================
  useEffect(() => {
    if (!signControlled) return;
    if (selectedSign && selectedSign !== sign) {
      setSign(selectedSign as ZodiacSign);
    }
  }, [selectedSign, signControlled, sign]);

  // ===========================================================================
  // Zodiyak SVG preload — fires on sign change. Result lives on a ref so the
  // canvas draw loop can read it synchronously without re-rendering.
  // ===========================================================================
  useEffect(() => {
    const img = new Image();
    img.src = `/SVG/zodiyak-${sign.toLowerCase()}.svg`;
    img.onload  = () => { zodiyakImgRef.current = img; };
    img.onerror = () => { zodiyakImgRef.current = null; };
  }, [sign]);

  // Preload the brand favicon once, then immediately tint a white silhouette copy.
  // source-in composite: opaque favicon pixels → white; transparent stays transparent.
  // Offscreen canvas is 2× the pill draw size for sub-pixel crispness when scaled down.
  useEffect(() => {
    const img = new Image();
    img.src = "/favicon.svg";
    img.onload = () => {
      signatureGlyphRef.current = img;
      const offW = SIG_GLYPH_W * 2;
      const offH = SIG_GLYPH_H * 2;
      const off  = document.createElement("canvas");
      off.width  = offW;
      off.height = offH;
      const octx = off.getContext("2d");
      if (octx) {
        octx.drawImage(img, 0, 0, offW, offH);
        octx.globalCompositeOperation = "source-in";
        octx.fillStyle = "#ffffff";
        octx.fillRect(0, 0, offW, offH);
        signatureGlyphWhiteRef.current = off;
      }
    };
    img.onerror = () => {
      signatureGlyphRef.current      = null;
      signatureGlyphWhiteRef.current = null;
    };
  }, []);

  // ===========================================================================
  // Preview carousel — two independent timers so background and caption drift
  // apart and the preview feels organic rather than flipping in unison.
  // ===========================================================================

  // Background cycle: 1800ms, unchanged from prior behavior.
  useEffect(() => {
    if (step !== "style") return;
    const id = window.setInterval(() => {
      setExampleIdx((i) => (i + 1) % PLACEHOLDER_BGS.length);
    }, 1800);
    return () => window.clearInterval(id);
  }, [step]);

  // Caption cycle: 2400ms, only while user hasn't interacted with the opener.
  // Fades the caption out, updates demo values, fades back in.
  useEffect(() => {
    if (step !== "style" || hasInteracted) return;
    let fadeTimer: ReturnType<typeof setTimeout> | null = null;
    const id = window.setInterval(() => {
      setCaptionVisible(false);
      fadeTimer = setTimeout(() => {
        setDemoOpenerIdx(Math.floor(Math.random() * OPENER_OPTIONS.length));
        setDemoPosIdx(Math.floor(Math.random() * PREVIEW_POSITIONS.length));
        setDemoColorIdx(Math.floor(Math.random() * PREVIEW_COLORS.length));
        // ±25% of 26px base — changes while opacity is 0 so no visible jump.
        setDemoFontSize(Math.round(26 * (0.75 + Math.random() * 0.5)));
        setCaptionVisible(true);
        fadeTimer = null;
      }, 220);
    }, 2400);
    return () => {
      window.clearInterval(id);
      if (fadeTimer !== null) clearTimeout(fadeTimer);
      setCaptionVisible(true);
    };
  }, [step, hasInteracted]);

  // Hide the global feedback button while the camera viewfinder is open.
  useEffect(() => {
    if (step === "camera") {
      document.body.classList.add("crushhh-camera-open");
    } else {
      document.body.classList.remove("crushhh-camera-open");
    }
    return () => { document.body.classList.remove("crushhh-camera-open"); };
  }, [step]);

  // WebCodecs support detection — async on mount.
  // Record button is hidden until this resolves (default videoSupported=false).
  useEffect(() => {
    void (async () => {
      if (typeof VideoEncoder === "undefined") return;
      try {
        const cfg: VideoEncoderConfigExt = {
          codec: VIDEO_CODEC,
          width: CANVAS_W,
          height: CANVAS_H,
          framerate: VIDEO_FPS,
          bitrate: VIDEO_BITRATE,
          avc: { format: "avc" },
        };
        const result = await VideoEncoder.isConfigSupported(cfg as VideoEncoderConfig);
        setVideoSupported(result.supported ?? false);
      } catch {
        // unsupported
      }
    })();
  }, []);

  useEffect(() => {
    setVideoCaptionOpener(openerValue);
    videoCaptionOpenerRef.current = openerValue;
  }, [openerValue]);

  useEffect(() => {
    videoCaptionOpenerRef.current = videoCaptionOpener;
  }, [videoCaptionOpener]);

  // Revoke video object URL when navigating away from review (in-session retakes / Back).
  useEffect(() => {
    if (step !== "review" && videoObjectUrlRef.current) {
      URL.revokeObjectURL(videoObjectUrlRef.current);
      videoObjectUrlRef.current = null;
      setCapturedVideoUrl(null);
    }
  }, [step]);

  // Revoke on full unmount (covers the case where component closes while on review).
  useEffect(() => {
    return () => {
      if (videoObjectUrlRef.current) URL.revokeObjectURL(videoObjectUrlRef.current);
    };
  }, []);

  // ===========================================================================
  // Overlay-only paint — used by the live camera RAF loop in the Snapchat-style
  // viewfinder. Clears the canvas to transparent and draws only the frame
  // overlay layers (gradient scrim, caption, Zodiyak, footer bar) so the video
  // element shows through underneath. The photo background, shadow, and border
  // are omitted — those are capture-only layers drawn by paintFrame.
  // ===========================================================================
  // Live-preview overlay — scrim only, NO caption. Caption is applied in the
  // post-capture edit step. Removing captionText from the dep array means the
  // camera useEffect ([step, paintOverlay]) no longer restarts the RAF loop
  // whenever the caption changes — a side-effect benefit of the new flow.
  const paintOverlay = useCallback((ctx: CanvasRenderingContext2D) => {
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

    // Gradient scrim — kept in preview so the viewfinder doesn't look raw.
    const grad = ctx.createLinearGradient(0, CANVAS_H * 0.4, 0, CANVAS_H);
    grad.addColorStop(0, "rgba(0,0,0,0)");
    grad.addColorStop(1, "rgba(0,0,0,0.82)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, CANVAS_H * 0.4, CANVAS_W, CANVAS_H * 0.6);
  }, []);

  // ===========================================================================
  // Live camera — getUserMedia + RAF render loop. Stream lifecycle keyed on
  // [step]; the initial stream is acquired with the current facingRef. Flips
  // are handled imperatively in handleFlipCamera so the effect doesn't tear
  // down and re-acquire the stream on every flip (which on some devices
  // returns the same camera anyway since facingMode is a soft constraint).
  // The tick reads facingRef.current each frame so the mirror toggle picks
  // up flips without a closure rebuild.
  //
  // captionText/sign are stable while step === "camera" (user can't navigate
  // back to the picker steps without leaving camera, which triggers cleanup),
  // so they're not in the dep array — re-running this effect on caption change
  // would needlessly re-acquire the camera stream.
  // ===========================================================================
  useEffect(() => {
    if (step !== "camera") return;
    let mounted = true;

    async function start() {
      setError(null);
      try {
        if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
          setError("Camera not available on this device or browser.");
          return;
        }

        // Wait for any pending font loads so canvas-rendered text uses Fredoka
        // and not the sans-serif fallback. Fonts are preloaded in index.html
        // so this is usually a no-op by the time the user reaches this step.
        try {
          await document.fonts.ready;
        } catch {
          // Non-fatal — proceed with whatever's loaded.
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facingRef.current },
          audio: false,
        });

        if (!mounted) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;

        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          video.muted = true;
          video.playsInline = true;
          await video.play();
        }

        const tick = () => {
          if (!mounted) return;
          const canvas = canvasRef.current;
          if (!canvas) {
            rafRef.current = requestAnimationFrame(tick);
            return;
          }
          const ctx = canvas.getContext("2d");
          if (!ctx) return;

          if (isRecordingRef.current) {
            recFramesRef.current++;
            const vid = videoRef.current;
            ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
            recVwRef.current = vid?.videoWidth ?? 0;
            if (vid && vid.videoWidth > 0) {
              recDrewVideoRef.current++;
              const vw = vid.videoWidth, vh = vid.videoHeight;
              const ta = CANVAS_W / CANVAS_H, va = vw / vh;
              let sx = 0, sy = 0, sw = vw, sh = vh;
              if (va > ta) { sw = vh * ta; sx = (vw - sw) / 2; }
              else         { sh = vw / ta; sy = (vh - sh) / 2; }
              if (facingRef.current === "user") {
                ctx.save();
                ctx.translate(CANVAS_W / 2, CANVAS_H / 2);
                ctx.scale(-1, 1);
                ctx.drawImage(vid, sx, sy, sw, sh, -CANVAS_W / 2, -CANVAS_H / 2, CANVAS_W, CANVAS_H);
                ctx.restore();
              } else {
                ctx.drawImage(vid, sx, sy, sw, sh, 0, 0, CANVAS_W, CANVAS_H);
              }
            }
            paintOverlay(ctx);
            if (DRAW_CAPTION_IN_RECORDING) {
              const fullCaption = `${videoCaptionOpenerRef.current}\ncrushhh me back`;
              drawCaption(ctx, fullCaption, CAPTION_FONT_SIZE_DEFAULT, CAPTION_CENTER_X_DEFAULT, CAPTION_CENTER_Y_DEFAULT);
            }
            drawSignaturePill(ctx, signatureGlyphWhiteRef.current);

            // WebCodecs: encode at 30fps (wall-clock throttle keeps within Level 4.0 MBPS limits).
            const now = performance.now();
            const enc = videoEncoderRef.current;
            if (enc && enc.state === "configured" &&
                now - lastEncodeTimeRef.current >= 1000 / VIDEO_FPS) {
              lastEncodeTimeRef.current = now;
              const ts = Math.round((now - recordStartTimeRef.current) * 1000); // μs
              const keyFrame = encFrameCountRef.current % KEYFRAME_EVERY === 0;
              encFrameCountRef.current++;
              const frame = new VideoFrame(canvas, { timestamp: ts });
              enc.encode(frame, { keyFrame });
              frame.close();
            }
          } else {
            // Overlay-only: canvas transparent except for scrim; video element shows through.
            paintOverlay(ctx);
          }

          rafRef.current = requestAnimationFrame(tick);
        };

        rafRef.current = requestAnimationFrame(tick);
      } catch (err: unknown) {
        if (!mounted) return;
        const name = (err as { name?: string })?.name ?? "";
        if (name === "NotAllowedError" || name === "PermissionDeniedError") {
          setError("Camera access denied. Please allow camera access in your browser settings.");
        } else if (name === "NotFoundError" || name === "OverconstrainedError") {
          setError("No camera matching the requested configuration.");
        } else {
          setError("Unable to start the camera. Please try again.");
        }
      }
    }

    start();

    return () => {
      mounted = false;
      if (countdownIntervalRef.current !== null) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      isRecordingRef.current      = false;
      finalizeRecordingRef.current = null;
      setIsRecording(false);
      const enc = videoEncoderRef.current;
      videoEncoderRef.current = null;
      if (enc && enc.state !== "closed") {
        try { enc.close(); } catch { /* ignore */ }
      }
      muxerRef.current = null;
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      const s = streamRef.current;
      if (s) {
        s.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    };
    // captionText/sign intentionally excluded — see header comment.
    // facing intentionally excluded — flips are handled imperatively.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, paintOverlay]);

  // -------- Handlers --------

  // Drag-to-reposition caption. The overlay div captures the pointer on
  // pointerdown so moves and releases are received even if the pointer
  // leaves the div mid-drag (scrolled, fast flick, etc.).
  const cssToCanvas = (
    el: HTMLElement,
    clientX: number,
    clientY: number,
  ): { x: number; y: number } => {
    const rect = el.getBoundingClientRect();
    return {
      x: Math.round(((clientX - rect.left) / rect.width)  * CANVAS_W),
      y: Math.round(((clientY - rect.top)  / rect.height) * CANVAS_H),
    };
  };

  const handleDragStart = (e: React.PointerEvent<HTMLDivElement>) => {
    isDragging.current = true;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    if (editCanvasRef.current) {
      const p = cssToCanvas(editCanvasRef.current, e.clientX, e.clientY);
      dragOffsetRef.current = { x: p.x - captionPosRef.current.x, y: p.y - captionPosRef.current.y };
    }
  };

  const handleDragMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging.current || !editCanvasRef.current) return;
    const p = cssToCanvas(editCanvasRef.current, e.clientX, e.clientY);
    const pos = clampCaptionPos({ x: p.x - dragOffsetRef.current.x, y: p.y - dragOffsetRef.current.y });
    captionPosRef.current = pos;
  };

  const handleDragEnd = () => {
    isDragging.current = false;
  };

  const handleFlipCamera = async () => {
    if (step !== "camera") return;
    const next: "user" | "environment" = facing === "user" ? "environment" : "user";

    // Stop the current MediaStream fully before requesting a new one — on some
    // mobile browsers a new getUserMedia call while the previous track is
    // still active silently returns the same camera.
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }

    try {
      // `{ exact: next }` — strict constraint. Without `exact`, browsers treat
      // facingMode as a soft preference and frequently return the camera that's
      // already in use, so the flip silently no-ops.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { exact: next } },
        audio: false,
      });
      streamRef.current = stream;
      // Update facingRef first so the next RAF tick mirrors correctly, then
      // the state (purely for any UI bits that read the React value).
      facingRef.current = next;
      setFacing(next);

      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        video.muted = true;
        video.playsInline = true;
        await video.play();
      }
    } catch (err: unknown) {
      const name = (err as { name?: string })?.name ?? "";
      if (name === "NotFoundError" || name === "OverconstrainedError") {
        setError("This device doesn't have a matching camera to switch to.");
      } else if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        setError("Camera access denied. Please allow camera access in your browser settings.");
      } else {
        setError("Unable to switch camera. Please try again.");
      }
    }
  };

  const startRecording = useCallback(() => {
    const canvas = canvasRef.current;
    const video  = videoRef.current;
    if (!canvas || !video) return;

    void (async () => {
      if (video.readyState < 2) {
        await new Promise<void>((resolve) => {
          const done = () => resolve();
          video.addEventListener("canplay",    done, { once: true });
          video.addEventListener("loadeddata", done, { once: true });
        });
      }
      if (video.paused) {
        try { await video.play(); } catch { /* non-fatal */ }
      }

      if (videoObjectUrlRef.current) {
        URL.revokeObjectURL(videoObjectUrlRef.current);
        videoObjectUrlRef.current = null;
      }
      setCapturedVideoUrl(null);
      videoBlobRef.current = null;

      recFramesRef.current     = 0;
      recDrewVideoRef.current  = 0;
      recVwRef.current         = 0;
      encFrameCountRef.current = 0;

      // Muxer: in-memory ArrayBuffer target, moov at start for instant playback.
      const target = new ArrayBufferTarget();
      const muxer = new Muxer({
        target,
        video: { codec: "avc", width: CANVAS_W, height: CANVAS_H },
        fastStart: "in-memory",
        firstTimestampBehavior: "offset",
      });
      muxerRef.current = muxer;

      // Encoder: H.264 Constrained Baseline L4.0 — hardware-accelerated on iOS + Android.
      // avc format (AVCC) required by mp4-muxer (not annexb).
      const encoder = new VideoEncoder({
        output: (chunk, meta) => { muxer.addVideoChunk(chunk, meta); },
        error:  (e)           => { console.error("VideoEncoder error:", e); },
      });
      const encoderCfg: VideoEncoderConfigExt = {
        codec:     VIDEO_CODEC,
        width:     CANVAS_W,
        height:    CANVAS_H,
        bitrate:   VIDEO_BITRATE,
        framerate: VIDEO_FPS,
        avc:       { format: "avc" },
      };
      encoder.configure(encoderCfg as VideoEncoderConfig);
      videoEncoderRef.current = encoder;

      const startNow = performance.now();
      recordStartTimeRef.current = startNow;
      lastEncodeTimeRef.current  = startNow;

      // Defined here so both auto-stop (countdown) and manual stop share the same path.
      finalizeRecordingRef.current = async () => {
        finalizeRecordingRef.current = null;
        const enc = videoEncoderRef.current;
        videoEncoderRef.current = null;
        const mux = muxerRef.current;
        muxerRef.current = null;
        if (!enc || !mux) return;
        try { await enc.flush(); } catch { /* partial data is fine */ }
        try { enc.close(); } catch { /* ignore */ }
        try {
          mux.finalize();
          const blob = new Blob([mux.target.buffer], { type: "video/mp4" });
          videoBlobRef.current = blob;
          const url = URL.createObjectURL(blob);
          videoObjectUrlRef.current = url;
          setCapturedVideoUrl(url);
          setCaptureSource("video");
          setStep("review");
        } catch (e) {
          console.error("Muxer finalize error:", e);
          setError("Failed to save video. Please try again.");
        }
      };

      isRecordingRef.current = true;
      setIsRecording(true);
      setRecordSecsLeft(MAX_RECORD_SECONDS);

      let secsLeft = MAX_RECORD_SECONDS;
      countdownIntervalRef.current = setInterval(() => {
        secsLeft -= 1;
        setRecordSecsLeft(secsLeft);
        if (secsLeft <= 0) {
          if (countdownIntervalRef.current !== null) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
          isRecordingRef.current = false;
          setIsRecording(false);
          setRecordSecsLeft(0);
          void finalizeRecordingRef.current?.();
        }
      }, 1000);
    })();
  }, [paintOverlay]);

  const stopRecording = useCallback(() => {
    if (countdownIntervalRef.current !== null) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    isRecordingRef.current = false;
    setIsRecording(false);
    setRecordSecsLeft(0);
    void finalizeRecordingRef.current?.();
  }, []);

  const handleCapture = useCallback(() => {
    const canvas = canvasRef.current;
    const video  = videoRef.current;
    if (!canvas || !video) return;

    // Pause the RAF loop so the captured frame is deterministic.
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      setError("Failed to capture image. Please try again.");
      return;
    }

    // Capture a CLEAN still — photo only, no scrim, no caption.
    // Scrim and caption are composited in the edit step so the user can
    // adjust size and position after shooting (native social app pattern).
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    if (vw > 0 && vh > 0) {
      const targetAspect = CANVAS_W / CANVAS_H;
      const videoAspect  = vw / vh;
      let sx = 0, sy = 0, sw = vw, sh = vh;
      if (videoAspect > targetAspect) {
        sw = vh * targetAspect;
        sx = (vw - sw) / 2;
      } else {
        sh = vw / targetAspect;
        sy = (vh - sh) / 2;
      }
      if (facingRef.current === "user") {
        ctx.save();
        ctx.translate(CANVAS_W / 2, CANVAS_H / 2);
        ctx.scale(-1, 1);
        ctx.drawImage(video, sx, sy, sw, sh, -CANVAS_W / 2, -CANVAS_H / 2, CANVAS_W, CANVAS_H);
        ctx.restore();
      } else {
        ctx.drawImage(video, sx, sy, sw, sh, 0, 0, CANVAS_W, CANVAS_H);
      }
    }

    // toDataURL avoids the blob URL path (URL.createObjectURL is blocked by CSP).
    let dataUrl: string;
    try {
      dataUrl = canvas.toDataURL("image/png");
    } catch {
      setError("Failed to capture image. Please try again.");
      return;
    }

    // Pre-decode the still into an Image object so edit-step redraws are cheap
    // (~1-5ms drawImage on an already-decoded object vs. re-parsing the data URL
    // each time). stillImageRef is cleared on Retake.
    const img = new Image();
    img.src = dataUrl;
    stillImageRef.current = img;

    setCaptureSource("camera");
    setCapturedUrl(dataUrl);
    logEvent("photoFrame.capture", { sign, captionText });
    setStep("edit");
  }, [sign, captionText]);

  const handleRetake = () => {
    setCapturedUrl(null);
    stillImageRef.current = null;
    setError(null);
    setStep("camera");
  };

  // Gallery source: FileReader.readAsDataURL keeps us on the data-URL path
  // (URL.createObjectURL is blocked by CSP). Center-crops to CANVAS_W×CANVAS_H
  // via an offscreen canvas — same aspect-ratio logic as handleCapture. Writes
  // capturedUrl + stillImageRef then advances straight to the edit step,
  // skipping the live-camera step entirely for this route.
  const handleGalleryFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so re-selecting the same file fires onChange again.
    e.target.value = "";
    if (!file) return;

    const reader = new FileReader();
    reader.onerror = () => {
      setError("Failed to read image file. Please try again.");
    };
    reader.onload = () => {
      const rawDataUrl = reader.result as string;
      const raw = new Image();
      raw.onerror = () => {
        setError("Failed to load image. Please try a different photo.");
      };
      raw.onload = () => {
        // Center-crop to 9:16 portrait — mirrors handleCapture's crop logic.
        const offscreen = document.createElement("canvas");
        offscreen.width  = CANVAS_W;
        offscreen.height = CANVAS_H;
        const ctx = offscreen.getContext("2d");
        if (!ctx) { setError("Failed to process image. Please try again."); return; }

        const iw = raw.naturalWidth;
        const ih = raw.naturalHeight;
        const targetAspect = CANVAS_W / CANVAS_H;
        const imgAspect    = iw / ih;
        let sx = 0, sy = 0, sw = iw, sh = ih;
        if (imgAspect > targetAspect) {
          sw = ih * targetAspect;
          sx = (iw - sw) / 2;
        } else {
          sh = iw / targetAspect;
          sy = (ih - sh) / 2;
        }
        ctx.drawImage(raw, sx, sy, sw, sh, 0, 0, CANVAS_W, CANVAS_H);

        let dataUrl: string;
        try {
          dataUrl = offscreen.toDataURL("image/png");
        } catch {
          setError("Failed to process image. Please try again.");
          return;
        }

        // Pre-decode the cropped still for fast edit-step redraws — same
        // pattern as handleCapture so redrawEdit gets an already-decoded Image.
        const cropped = new Image();
        cropped.onload = () => {
          stillImageRef.current = cropped;
          setCaptureSource("gallery");
          setCapturedUrl(dataUrl);
          logEvent("photoFrame.capture", { sign, captionText });
          setStep("edit");
        };
        cropped.src = dataUrl;
      };
      raw.src = rawDataUrl;
    };
    reader.readAsDataURL(file);
  };

  // ===========================================================================
  // Edit step: event-driven canvas redraw (no RAF loop).
  // Draws the captured still + scrim + caption at current fontSizeRef /
  // captionPosRef values. Called on mount, slider change, and drag move.
  // captionText is stable during the edit step (dropdowns are in the style
  // step and gated before the camera opens).
  // ===========================================================================
  const redrawEdit = useCallback(() => {
    const canvas = editCanvasRef.current;
    const img    = stillImageRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.drawImage(img, 0, 0, CANVAS_W, CANVAS_H);

    // Gradient scrim — same as paintOverlay/paintFrame.
    const grad = ctx.createLinearGradient(0, CANVAS_H * 0.4, 0, CANVAS_H);
    grad.addColorStop(0, "rgba(0,0,0,0)");
    grad.addColorStop(1, "rgba(0,0,0,0.82)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, CANVAS_H * 0.4, CANVAS_W, CANVAS_H * 0.6);

    if (captionText) {
      drawCaption(ctx, captionText, fontSizeRef.current, captionPosRef.current.x, captionPosRef.current.y, captionColorRef.current);
    }
    drawSignaturePill(ctx, signatureGlyphWhiteRef.current);
  }, [captionText]);

  // RAF-gated redraw for drag moves — caps canvas redraws at one per frame
  // regardless of pointer event rate. All other redrawEdit callers remain synchronous.
  const scheduleEditRedraw = () => {
    if (redrawPendingRef.current) return;
    redrawPendingRef.current = true;
    requestAnimationFrame(() => { redrawPendingRef.current = false; redrawEdit(); });
  };

  // Draw the initial composite as soon as the edit canvas mounts.
  useEffect(() => {
    if (step !== "edit") return;
    redrawEdit();
  }, [step, redrawEdit]);

  // ===========================================================================
  // Confirm: defensive filterCaption re-check, final composite to edit canvas,
  // toDataURL → capturedUrl, advance to review. handleShare is untouched.
  // ===========================================================================
  const handleConfirm = useCallback(() => {
    let blocked = false;
    if (useOpenerCustom && customOpener.trim()) {
      const res = filterCaption(customOpener);
      if (!res.ok) { setOpenerFilterErr(res.reason); blocked = true; }
      else setOpenerFilterErr(null);
    } else { setOpenerFilterErr(null); }
    if (blocked) return;

    // Draw final composite — identical path to redrawEdit so WYSIWYG is exact.
    const canvas = editCanvasRef.current;
    const img    = stillImageRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.drawImage(img, 0, 0, CANVAS_W, CANVAS_H);
    const grad = ctx.createLinearGradient(0, CANVAS_H * 0.4, 0, CANVAS_H);
    grad.addColorStop(0, "rgba(0,0,0,0)");
    grad.addColorStop(1, "rgba(0,0,0,0.82)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, CANVAS_H * 0.4, CANVAS_W, CANVAS_H * 0.6);
    if (captionText) {
      drawCaption(ctx, captionText, fontSizeRef.current, captionPosRef.current.x, captionPosRef.current.y, captionColorRef.current);
    }
    drawSignaturePill(ctx, signatureGlyphWhiteRef.current);

    let dataUrl: string;
    try {
      dataUrl = canvas.toDataURL("image/png");
    } catch {
      setError("Failed to export image. Please try again.");
      return;
    }
    setCapturedUrl(dataUrl);
    setStep("review");
  }, [captionText, useOpenerCustom, customOpener]);

  const handleShare = useCallback(async () => {
    const isVideo = VIDEO_BOOTH_ENABLED && captureSource === "video";
    if (!isVideo && !capturedUrl) return;
    if (isVideo && !videoBlobRef.current) return;
    setSharing(true);
    logEvent("photoFrame.shareAttempted", { sign, captionText });
    setError(null);
    try {
      const mimeType = isVideo ? "video/mp4" : "image/png";
      const ext = isVideo ? "mp4" : "png";
      const filename = `crushhh-${sign.toLowerCase()}-${Date.now()}.${ext}`;

      let file: File;
      if (isVideo) {
        file = new File([videoBlobRef.current!], filename, { type: mimeType });
      } else {
        // Build the File from raw bytes — no blob URL involved.
        // Cast: Uint8Array is a valid BlobPart at runtime, but newer TS lib types
        // narrow it to Uint8Array<ArrayBufferLike> which doesn't structurally
        // match the File constructor's BlobPart signature. The cast bridges that.
        const bytes = dataUrlToBytes(capturedUrl!);
        file = new File([bytes as BlobPart], filename, { type: mimeType });
      }

      // Prefer Web Share API with files when supported. Pass the same
      // shareData to both canShare() and share() — passing different
      // objects can cause canShare to return true while share() rejects.
      const shareData: ShareData = { files: [file], ...(isVideo ? {} : { text: captionText }) };
      if (
        typeof navigator !== "undefined" &&
        typeof navigator.canShare === "function" &&
        navigator.canShare(shareData) &&
        typeof navigator.share === "function"
      ) {
        try {
          await navigator.share(shareData);
          logEvent("photoFrame.shareSucceeded", { sign, captionText, method: "native" });
          onShareComplete?.();
          return;
        } catch (shareErr) {
          if ((shareErr as { name?: string })?.name === "AbortError") return;
          // Non-AbortError: fall through to download fallback.
        }
      }

      // Fallback: download directly. Video uses the live object URL; photo uses the data URL.
      if (isVideo && videoObjectUrlRef.current) {
        const a = document.createElement("a");
        a.href = videoObjectUrlRef.current;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } else {
        downloadDataUrl(capturedUrl!, filename);
      }
      logEvent("photoFrame.shareSucceeded", { sign, captionText, method: "download" });
      onShareComplete?.();
    } catch {
      setError("Sharing failed. Please try again.");
    } finally {
      setSharing(false);
    }
  }, [capturedUrl, captureSource, sign, captionText, onShareComplete]);

  // -------- Styles --------

  const cardStyle: React.CSSProperties = {
    background: BRAND.cream,
    border: `3px solid ${BRAND.ink}`,
    borderRadius: 20,
    boxShadow: `6px 6px 0px ${BRAND.ink}`,
    padding: "20px 16px",
    boxSizing: "border-box",
    width: "100%",
    maxWidth: 420,
  };

  const primaryButton = (disabled: boolean): React.CSSProperties => ({
    width: "100%",
    background: BRAND.coral,
    color: BRAND.white,
    fontFamily: "'Fredoka', sans-serif",
    fontWeight: 700,
    fontSize: 18,
    border: `3px solid ${BRAND.ink}`,
    borderRadius: 100,
    boxShadow: `4px 4px 0px ${BRAND.ink}`,
    padding: "14px 28px",
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    boxSizing: "border-box",
    transition: "transform 0.12s ease, opacity 0.12s ease",
  });

  const secondaryButton: React.CSSProperties = {
    width: "100%",
    background: BRAND.white,
    color: BRAND.ink,
    fontFamily: "'Fredoka', sans-serif",
    fontWeight: 600,
    fontSize: 16,
    border: `2.5px solid ${BRAND.ink}`,
    borderRadius: 100,
    boxShadow: `3px 3px 0px ${BRAND.ink}`,
    padding: "10px 24px",
    cursor: "pointer",
    boxSizing: "border-box",
  };

  const headingStyle: React.CSSProperties = {
    fontFamily: "'Fredoka', sans-serif",
    fontWeight: 700,
    fontSize: 24,
    color: BRAND.ink,
    margin: "2px 0 8px",
    textAlign: "center",
  };


  const errorBanner: React.CSSProperties = {
    padding: 12,
    background: "rgba(196, 75, 46, 0.12)",
    border: `1.5px solid ${BRAND.coral}`,
    borderRadius: 12,
    marginBottom: 16,
    fontFamily: "'Nunito', sans-serif",
    fontSize: 13,
    color: BRAND.coral,
  };

  const selectStyle: React.CSSProperties = {
    width: "100%",
    padding: "12px 16px",
    fontFamily: "'Fredoka', sans-serif",
    fontWeight: 600,
    fontSize: 16,
    color: BRAND.ink,
    background: BRAND.white,
    border: `2.5px solid ${BRAND.ink}`,
    borderRadius: 14,
    cursor: "pointer",
    boxSizing: "border-box",
  };

  // -------- Render --------

  return (
    <div
      ref={containerRef}
      style={{
        minHeight: (step === "camera" || step === "edit") ? "100dvh" : undefined,
        padding: (step === "camera" || step === "edit")
          ? "0"
          : "max(24px, env(safe-area-inset-top)) 12px max(48px, env(safe-area-inset-bottom))",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        fontFamily: "'Nunito', sans-serif",
        boxSizing: "border-box",
      }}
    >
      {/* Hidden file input — always mounted so fileInputRef stays valid after
          the style step unmounts (required for gallery redo from review step). */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={handleGalleryFile}
      />
      <div style={{
        ...cardStyle,
        position: 'relative',
        ...(step === "camera" ? {
          maxWidth: '100%',
          width: '100%',
          minHeight: '100dvh',
          borderRadius: 0,
          border: 'none',
          boxShadow: 'none',
          padding: 0,
          background: BRAND.ink,
          overflow: 'hidden',
        } : {}),
        ...(step === "edit" ? {
          maxWidth: '100%',
          width: '100%',
          minHeight: '100dvh',
          borderRadius: 0,
          border: 'none',
          boxShadow: 'none',
          padding: 0,
          background: BRAND.ink,
          overflow: 'hidden',
        } : {}),
      }}>
        <button
          onClick={() => onDismiss?.()}
          style={{
            position: 'absolute',
            top: 12,
            right: 12,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 22,
            color: '#888',
            lineHeight: 1,
            zIndex: 10,
          }}
          aria-label="Close"
        >
          ✕
        </button>
        {/* ---------- Step 1: Caption Builder + Live Preview ---------- */}
        {step === "style" && (
          <>
            <h2 style={headingStyle}>Soft Launch Your Crush</h2>

            {/* Carousel — background cycles every 1800ms; caption/position/color
                cycle every 2400ms independently. Switches to live user selection
                once they interact with the opener dropdown. */}
            <div style={{
              position: "relative",
              marginBottom: 16,
              height: "65vh",
              overflow: "hidden",
              borderRadius: 12,
            }}>
              {/* Background image */}
              <div style={{
                position: "absolute",
                inset: 0,
                backgroundImage: `url(${PLACEHOLDER_BGS[exampleIdx]})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}>
                {/* Dark scrim — fades in from bottom */}
                <div style={{
                  position: "absolute",
                  left: 0, right: 0, bottom: 0,
                  height: "60%",
                  background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.85) 100%)",
                  pointerEvents: "none",
                }} />
              </div>
              {/* Caption overlay — stable DOM element so opacity transition works */}
              <div style={{
                position: "absolute",
                left:      previewPos.left,
                right:     previewPos.right,
                top:       previewPos.top,
                bottom:    previewPos.bottom,
                color:     previewColor,
                textAlign: previewPos.textAlign,
                fontFamily: "system-ui, -apple-system, sans-serif",
                fontWeight: 800,
                fontSize:  hasInteracted ? "min(7vw, 26px)" : `${demoFontSize}px`,
                lineHeight: 1.15,
                // Dual halo: white lift for dark text on dark scrim; dark drop for light text on bright areas.
                textShadow: "0 1px 3px rgba(255,255,255,0.45), 0 2px 8px rgba(0,0,0,0.65)",
                opacity:    hasInteracted ? 1 : (captionVisible ? 1 : 0),
                transition: hasInteracted ? "none" : "opacity 0.2s ease",
              }}>
                {hasInteracted ? (
                  <>
                    <span style={{ color: BRAND.coral }}>{openerValue}</span>
                    <br />
                    <span style={{ color: BRAND.white }}>crushhh me back</span>
                  </>
                ) : (
                  <>{previewOpener}<br />crushhh me back</>
                )}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 16 }}>
              {/* Opener slot */}
              <div>
                {useOpenerCustom ? (
                  <>
                    <div style={{ position: "relative" }}>
                      <input
                        type="text"
                        value={customOpener}
                        maxLength={CAPTION_MAX_CHARS}
                        onChange={(e) => { setCustomOpener(e.target.value.toLowerCase()); setOpenerFilterErr(null); }}
                        placeholder="write your own…"
                        style={{ ...selectStyle, cursor: "text", paddingRight: 52 }}
                      />
                      <span style={{
                        position: "absolute", right: 12, top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: 12, pointerEvents: "none",
                        fontFamily: "'Nunito', sans-serif",
                        color: customOpener.length >= CAPTION_MAX_CHARS ? BRAND.coral : "rgba(26,26,26,0.7)",
                      }}>
                        {customOpener.length}/{CAPTION_MAX_CHARS}
                      </span>
                    </div>
                    {openerFilterErr && (
                      <p style={{ margin: "4px 0 0", fontSize: 13, color: BRAND.coral, fontFamily: "'Nunito', sans-serif" }}>
                        {openerFilterErr}
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => { setUseOpenerCustom(false); setCustomOpener(""); setOpenerFilterErr(null); }}
                      style={{ background: "none", border: "none", cursor: "pointer", display: "inline-flex", alignItems: "center", minHeight: 44, minWidth: 44, padding: "0 14px", fontSize: 14, color: BRAND.coral, fontFamily: "'Nunito', sans-serif", fontWeight: 600 }}
                    >
                      use presets
                    </button>
                  </>
                ) : (
                  <select
                    value={opener}
                    onChange={(e) => {
                      if (e.target.value === CUSTOM_OPTION_VALUE) {
                        setUseOpenerCustom(true);
                        setCustomOpener("");
                      } else {
                        setOpener(e.target.value);
                      }
                    }}
                    style={selectStyle}
                  >
                    <option value={CUSTOM_OPTION_VALUE}>write your own</option>
                    {OPENER_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                onClick={() => {
                  let blocked = false;
                  if (useOpenerCustom && customOpener.trim()) {
                    const res = filterCaption(customOpener);
                    if (!res.ok) { setOpenerFilterErr(res.reason); blocked = true; }
                    else { setOpenerFilterErr(null); }
                  } else { setOpenerFilterErr(null); }
                  if (blocked) return;
                  setStep("camera");
                  containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                style={primaryButton(false)}
              >
                Capture
              </button>
              <button
                onClick={() => {
                  let blocked = false;
                  if (useOpenerCustom && customOpener.trim()) {
                    const res = filterCaption(customOpener);
                    if (!res.ok) { setOpenerFilterErr(res.reason); blocked = true; }
                    else { setOpenerFilterErr(null); }
                  } else { setOpenerFilterErr(null); }
                  if (blocked) return;
                  fileInputRef.current?.click();
                }}
                style={secondaryButton}
              >
                Choose from Gallery
              </button>
              <button
                onClick={() => onDismiss?.()}
                style={secondaryButton}
              >
                Back
              </button>
            </div>
          </>
        )}

        {/* ---------- Step 4: Camera (Snapchat-style full-screen viewfinder) --- */}
        {step === "camera" && (
          <div style={{
            position: "relative",
            width: "min(100%, calc(100dvh * 9 / 16))",
            aspectRatio: "9 / 16",
            marginLeft: "auto",
            marginRight: "auto",
          }}>
            {/* Video fills the container — always mounted so the stream ref stays valid */}
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
                transform: facing === "user" ? "scaleX(-1)" : "none",
              }}
            />
            {/* Canvas overlay — transparent except for the gradient scrim.
                Caption is suppressed from the live preview (applied in edit step).
                paintOverlay now draws scrim only — no caption, no RAF restarts
                from captionText changes. */}
            <canvas
              ref={canvasRef}
              width={CANVAS_W}
              height={CANVAS_H}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                display: "block",
              }}
            />

            {/* Drag-to-reposition overlay — covers viewfinder above the button row.
                zIndex 5 sits above canvas (no explicit z) and below buttons (z=10).
                touchAction:none suppresses browser scroll/zoom so pointer events
                are received without delay on mobile. */}
            <div
              onPointerDown={handleDragStart}
              onPointerMove={handleDragMove}
              onPointerUp={handleDragEnd}
              onPointerCancel={handleDragEnd}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: "18%",
                zIndex: 5,
                cursor: "grab",
                touchAction: "none",
              }}
            />

            {/* Error banner — centered float over dark background */}
            {error && (
              <div style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "80%",
                ...errorBanner,
                marginBottom: 0,
                zIndex: 20,
              }}>
                {error}
              </div>
            )}


            {/* Count Down ge — top center, only while recording */}
            {VIDEO_BOOTH_ENABLED && isRecording && (
              <div style={{
                position: "absolute",
                top: "8%",
                left: "50%",
                transform: "translateX(-50%)",
                background: "rgba(224,90,58,0.92)",
                color: BRAND.white,
                fontFamily: "'Fredoka', sans-serif",
                fontWeight: 700,
                fontSize: 20,
                padding: "6px 22px",
                borderRadius: 100,
                zIndex: 10,
                whiteSpace: "nowrap",
                letterSpacing: 1,
              }}>
                ● REC {recordSecsLeft}s
              </div>
            )}


            {/* Buttons — photo + record (pre-recording) / stop (during recording) */}
            <div style={{
              position: "absolute",
              bottom: "18%",
              left: 0, right: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 16,
              padding: "0 24px",
              zIndex: 10,
            }}>
              {VIDEO_BOOTH_ENABLED && isRecording ? (
                <button
                  onClick={stopRecording}
                  style={{
                    width: 72,
                    height: 72,
                    borderRadius: "50%",
                    background: BRAND.coral,
                    border: `3px solid ${BRAND.ink}`,
                    boxShadow: "6px 6px 0px #1A1A1A",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                  aria-label="Stop recording"
                >
                  <div style={{ width: 22, height: 22, background: BRAND.white, borderRadius: 3 }} />
                </button>
              ) : (
                <>
                  {/* Photo capture — circle shutter */}
                  <button
                    onClick={handleCapture}
                    onPointerDown={() => setPressing(true)}
                    onPointerUp={() => setPressing(false)}
                    onPointerLeave={() => setPressing(false)}
                    disabled={!!error}
                    aria-label="Take photo"
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      background: pressing ? "rgba(255,255,255,0.45)" : "rgba(255,255,255,0.75)",
                      border: `3px solid ${BRAND.ink}`,
                      boxShadow: "6px 6px 0px #1A1A1A",
                      transform: pressing ? "scale(0.95)" : "scale(1)",
                      transition: "background-color 0.15s ease, transform 0.1s ease",
                      cursor: error ? "not-allowed" : "pointer",
                      opacity: error ? 0.5 : 1,
                      flexShrink: 0,
                    }}
                  />
                  {/* Record button — only rendered if MediaRecorder is available */}
                  {VIDEO_BOOTH_ENABLED && videoSupported && (
                    <button
                      onClick={startRecording}
                      disabled={!!error}
                      aria-label="Record video"
                      style={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        background: error ? "rgba(224,90,58,0.4)" : BRAND.coral,
                        border: `3px solid ${BRAND.ink}`,
                        boxShadow: "4px 4px 0px #1A1A1A",
                        cursor: error ? "not-allowed" : "pointer",
                        opacity: error ? 0.5 : 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <div style={{ width: 18, height: 18, background: BRAND.white, borderRadius: "50%" }} />
                    </button>
                  )}
                  {/* Flip camera */}
                  <button
                    onClick={handleFlipCamera}
                    disabled={!!error}
                    aria-label="Flip camera"
                    style={{
                      background: "rgba(255,255,255,0.9)",
                      color: BRAND.ink,
                      border: "none",
                      borderRadius: "50%",
                      width: 56,
                      height: 56,
                      minWidth: 56,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: error ? "not-allowed" : "pointer",
                      opacity: error ? 0.5 : 1,
                      backdropFilter: "blur(4px)",
                      WebkitBackdropFilter: "blur(4px)",
                      flexShrink: 0,
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="36" height="36">
                      <path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46A7.93 7.93 0 0 0 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74A7.93 7.93 0 0 0 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
                    </svg>
                  </button>
                </>
              )}
            </div>
            {/* Back button — floating, bottom ~6%; hidden during recording */}
            <button
              onClick={() => setStep("style")}
              disabled={isRecording}
              style={{
                position: "absolute",
                bottom: "6%",
                left: "50%",
                transform: "translateX(-50%)",
                background: "rgba(255,255,255,0.1)",
                color: BRAND.white,
                border: "2px solid rgba(255,255,255,0.35)",
                borderRadius: 100,
                padding: "10px 28px",
                fontFamily: "'Fredoka', sans-serif",
                fontWeight: 600,
                fontSize: 14,
                cursor: "pointer",
                zIndex: 10,
                backdropFilter: "blur(4px)",
                WebkitBackdropFilter: "blur(4px)",
                whiteSpace: "nowrap",
                opacity: isRecording ? 0 : 1,
                pointerEvents: isRecording ? "none" : "auto",
              }}
            >
              Back
            </button>
          </div>
        )}

        {/* ---------- Edit step: caption on still ---------- */}
        {step === "edit" && (
          <div style={{
            position: "relative",
            width: "min(100%, calc(100dvh * 9 / 16))",
            aspectRatio: "9 / 16",
            marginLeft: "auto",
            marginRight: "auto",
          }}>
            <canvas
              ref={editCanvasRef}
              width={CANVAS_W}
              height={CANVAS_H}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                display: "block",
              }}
            />

            {/* Font-size slider — floating pill at top of edit canvas */}
            <div style={{
              position: "absolute",
              top: "5%",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 10,
              background: "rgba(0,0,0,0.45)",
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
              borderRadius: 100,
              padding: "6px 16px",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}>
              <span style={{ color: BRAND.white, fontSize: 11, fontFamily: "'Fredoka', sans-serif", opacity: 0.8, userSelect: "none" }}>A</span>
              <input
                type="range"
                min={48}
                max={120}
                step={4}
                value={fontSize}
                aria-label="Caption font size"
                style={{ width: 100, accentColor: BRAND.coral, cursor: "pointer" }}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  fontSizeRef.current = next;
                  setFontSize(next);
                  const clamped = clampCaptionPos(captionPosRef.current);
                  captionPosRef.current = clamped;
                  setCaptionPos(clamped);
                  redrawEdit();
                }}
              />
              <span style={{ color: BRAND.white, fontSize: 17, fontFamily: "'Fredoka', sans-serif", opacity: 0.8, userSelect: "none" }}>A</span>
            </div>

            {/* Caption color swatches — floating pill below font-size slider */}
            {(() => {
              const SWATCHES: { hex: string; label: string }[] = [
                { hex: "#FFFFFF", label: "White" },
                { hex: "#1A1A1A", label: "Black" },
                { hex: "#FFE600", label: "Yellow" },
                { hex: "#E05A3A", label: "Coral" },
                { hex: "#F0DFC8", label: "Cream" },
              ];
              return (
                <div style={{
                  position: "absolute",
                  top: "14%",
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 10,
                  background: "rgba(0,0,0,0.45)",
                  backdropFilter: "blur(6px)",
                  WebkitBackdropFilter: "blur(6px)",
                  borderRadius: 100,
                  padding: "6px 14px",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}>
                  {SWATCHES.map(({ hex, label }) => {
                    const active = captionColor === hex;
                    return (
                      <button
                        key={hex}
                        aria-label={`Caption color: ${label}`}
                        onClick={() => {
                          captionColorRef.current = hex;
                          setCaptionColor(hex);
                          redrawEdit();
                        }}
                        style={{
                          width: 26,
                          height: 26,
                          borderRadius: "50%",
                          background: hex,
                          border: active
                            ? "3px solid #FFFFFF"
                            : "2px solid rgba(255,255,255,0.35)",
                          boxShadow: active ? "0 0 0 1.5px #1A1A1A" : "none",
                          cursor: "pointer",
                          padding: 0,
                          flexShrink: 0,
                          transition: "border 0.1s, box-shadow 0.1s",
                        }}
                      />
                    );
                  })}
                </div>
              );
            })()}

            {/* Drag-to-reposition overlay — edit step variant.
                onPointerMove chains handleDragMove (updates captionPosRef) + redrawEdit
                so the still updates immediately on every drag tick. */}
            <div
              onPointerDown={handleDragStart}
              onPointerMove={(e) => { handleDragMove(e); scheduleEditRedraw(); }}
              onPointerUp={handleDragEnd}
              onPointerCancel={handleDragEnd}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: "18%",
                zIndex: 5,
                cursor: "grab",
                touchAction: "none",
              }}
            />

            {/* Retake + Confirm buttons — floating row, bottom ~6% */}
            <div style={{
              position: "absolute",
              bottom: "6%",
              left: 0,
              right: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 12,
              padding: "0 24px",
              zIndex: 10,
            }}>
              <button onClick={handleRetake} style={secondaryButton}>Retake</button>
              <button onClick={handleConfirm} style={primaryButton(false)}>Looks Good</button>
            </div>
          </div>
        )}

        {/* ---------- Step 5: Review + Share ---------- */}
        {step === "review" && (
          <>
            {(capturedUrl || capturedVideoUrl) && (
              <div style={{ marginBottom: 16 }}>
                {VIDEO_BOOTH_ENABLED && captureSource === "video" ? (
                  <>
                    <video
                      ref={reviewVideoRef}
                      src={capturedVideoUrl ?? undefined}
                      muted
                      autoPlay
                      controls
                      loop
                      playsInline
                      style={{ width: "100%", borderRadius: 12, display: "block", minHeight: 200, background: "#000" }}
                      onLoadedMetadata={() => {
                        // Use reviewVideoRef — React nullifies e.currentTarget before updater closures run.
                        const el = reviewVideoRef.current;
                        if (!el) return;
                        el.currentTime = 0.1;
                        el.play().catch(() => {});
                      }}
                    />
                  </>
                ) : (
                  <img
                    src={capturedUrl!}
                    alt="Captured frame"
                    style={{ width: "100%", borderRadius: 12, display: "block" }}
                  />
                )}
              </div>
            )}
            {error && <div style={errorBanner}>{error}</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button onClick={handleShare} disabled={sharing} style={primaryButton(sharing)} aria-label="Share">
                <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  <span>{sharing ? "Shipping..." : "Ship It"}</span>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    aria-hidden="true"
                  >
                    <circle cx="18" cy="5" r="3" fill="currentColor" stroke="none" />
                    <circle cx="6" cy="12" r="3" fill="currentColor" stroke="none" />
                    <circle cx="18" cy="19" r="3" fill="currentColor" stroke="none" />
                    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                  </svg>
                </span>
              </button>
              {/* Source-aware redo: reshoot on camera, re-pick on gallery.
                  Gallery redo re-triggers the always-mounted file input; cancel
                  returns to review with the current image intact (handleGalleryFile
                  returns early on no-file, step stays "review"). */}
              {captureSource === "gallery" ? (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sharing}
                  style={secondaryButton}
                >
                  Choose Another
                </button>
              ) : (
                <button onClick={handleRetake} disabled={sharing} style={secondaryButton}>
                  Retake
                </button>
              )}
              {/* Back — returns to the source-choice screen as a fresh attempt.
                  Clears image and source flag; caption choices are preserved. */}
              <button
                onClick={() => {
                  setCapturedUrl(null);
                  stillImageRef.current = null;
                  setCaptureSource(null);
                  setError(null);
                  setStep("style");
                }}
                disabled={sharing}
                style={secondaryButton}
              >
                Back
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
